// src/pages/TradeTracker/components/TradesTable.jsx
import React from 'react';
import TradeRowDetails from './TradeRowDetails';
import { formatNotional } from './utils/formatters';

export default function TradesTable({
  selectedDate,
  filteredTrades,
  valuationByTradeId,
  fullUSD,
  isGenerating, // Spinner state (from parent)
}) {
  const [expandedTradeId, setExpandedTradeId] = React.useState(null);
  const norm = (s) => String(s ?? '').trim();

  const formatDate = (str) => {
    if (!str) return '—';
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [y, m, d] = str.split('-').map(Number);
      return `${m}/${d}/${y}`;
    }
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return String(str);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-2">
        <h2 className="mb-3 text-lg font-semibold text-brand">Trade Details</h2>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="px-2 py-2 text-left text-xs font-medium text-muted uppercase tracking-wider">
                Details
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium text-muted uppercase tracking-wider">
                Trade ID
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium text-muted uppercase tracking-wider">
                Trade Type
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium text-muted uppercase tracking-wider">
                Trade Party
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium text-muted uppercase tracking-wider">
                Counterparty
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium text-muted uppercase tracking-wider">
                Trade Date
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium text-muted uppercase tracking-wider hidden lg:table-cell">
                Eff Date
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium text-muted uppercase tracking-wider hidden lg:table-cell">
                Term Date
              </th>
              <th className="px-2 py-2 text-right text-xs font-medium text-muted uppercase tracking-wider">
                Notional
              </th>
              <th className="px-2 py-2 text-right text-xs font-medium text-muted uppercase tracking-wider">
                Value
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {filteredTrades.map((t) => {
              const tid = norm(t.trade_id);
              const v = valuationByTradeId.get(tid);
              const isExpanded = expandedTradeId === tid;

              return (
                <React.Fragment key={tid}>
                  <tr className="border-t border-border">
                    <td className="p-2 text-foreground">
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        onClick={() => setExpandedTradeId(isExpanded ? null : tid)}
                        className="inline-flex items-center justify-center w-20 h-8 rounded text-sm font-medium
                                   bg-secondary text-secondary-foreground hover:bg-secondary/80 transition
                                   focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                        title="Show trade details"
                      >
                        {isExpanded ? 'Hide' : 'View'}
                      </button>
                    </td>
                    <td className="p-2 text-foreground break-words">{t.trade_id}</td>
                    <td className="p-2 text-foreground break-words">{t.trade_type}</td>
                    <td className="p-2 text-foreground break-words">{t.leg1_party}</td>
                    <td className="p-2 text-foreground break-words">{t.leg2_party}</td>
                    <td className="p-2 text-foreground">{formatDate(t.trade_date)}</td>
                    <td className="p-2 hidden lg:table-cell text-foreground">
                      {formatDate(t.effective_date)}
                    </td>
                    <td className="p-2 hidden lg:table-cell text-foreground">
                      {formatDate(t.termination_date)}
                    </td>
                    <td className="p-2 text-right text-foreground">
                      {v && v.notional_amount != null
                        ? formatNotional(Number(v.notional_amount), t.trade_type)
                        : '–'}
                    </td>
                    <td className="p-2 text-right text-foreground">
                      {v && v.total_value != null ? fullUSD(Number(v.total_value)) : '–'}
                    </td>
                  </tr>

                  {isExpanded && <TradeRowDetails t={t} v={v} selectedDate={selectedDate} />}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* PDF generation overlay */}
      {isGenerating && (
        <div className="absolute inset-0 flex items-center justify-center bg-app/60 z-10">
          <div className="flex flex-col items-center gap-3 bg-card px-6 py-4 rounded-lg shadow-lg border border-border">
            <div
              aria-hidden="true"
              className="animate-spin h-6 w-6 rounded-full border-4 border-muted border-t-brand"
            />
            <p className="text-app font-medium">Generating</p>
          </div>
        </div>
      )}
    </div>
  );
}
